import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useClassesStore } from '../../store/providers/Class';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './ClassesSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const ClassModal = React.lazy(() => import('./ClassModal'));

function Classes() {    
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, data, clearData } = useClassesStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_CLASSES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (classItem) => {
        setIsEditing(true);
        selectRecord({ classId: classItem.Id });
    };

    const refreshedHandler = () => {
        listRecords({ national: true });
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords({ national: true });
    };

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {            
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords({ national: true });
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'classes', text: 'Classes' }));
        listRecords({ national: true });

        setIsMounted(true);
    }, [hasPermission, isMounted, listRecords, dispatch]);

    return (
        <>
            <SideMenu activeKey="classes" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading Classes..." />}>
                        {
                            isEditing && <ClassModal
                                show={isEditing}
                                canEdit={canEdit}
                                title='Edit Class'
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Classes" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        keyField="Id"
                                        compType="Class"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        permissionEdit={canEdit}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        hideDelete={true}
                                        hideAdd={true}
                                        selected={(faq) => selectedHandler(faq)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(Classes));
